// OWNER: FRONTEND-DIALER agent. SPEC §15.3: my open tasks grouped Overdue / Today / Upcoming (complete, snooze, open
// the lead in the dialer) and the inbound voicemail box (listen, mark handled).
import '../dialer/dialer.css';
import { useEffect, useState } from 'preact/hooks';
import type { Task, TaskKind } from '../../../src/shared/api';
import { ApiFailure, errorMessage } from '../api';
import { navigate } from '../app';
import { Empty, ErrorBanner, fmtDateTime, timeAgo, toast } from '../components/ui';
import { Segmented, Spinner, useAsync } from '../components/ui-extra';
import { voicemailRowActions } from '../dialer/audio';
import { VoicemailAudio } from '../dialer/VoicemailAudio';
import { endpoints } from '../dialer/endpoints';
import { fmtPhone } from '../dialer/labels';
import { openMembership } from '../dialer/store';
import { fmtDuration } from '../dialer/time';
import { refreshMissed } from '../missed';
import { useMe } from '../me';

const KIND_LABEL: Record<TaskKind, string> = {
  callback: 'Callback', follow_up: 'Follow-up', email: 'Email', demo_build: 'Build demo', morning_text: 'Morning text',
  voicemail: 'Voicemail', number_check: 'Number check', other: 'Task',
};

function startOfDay(d: Date): number { return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); }

function group(tasks: Task[], now: Date): { overdue: Task[]; today: Task[]; upcoming: Task[] } {
  const endToday = startOfDay(now) + 86400_000;
  const sorted = [...tasks].sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  return {
    overdue: sorted.filter((t) => new Date(t.dueAt).getTime() < now.getTime()),
    today: sorted.filter((t) => { const ms = new Date(t.dueAt).getTime(); return ms >= now.getTime() && ms < endToday; }),
    upcoming: sorted.filter((t) => new Date(t.dueAt).getTime() >= endToday),
  };
}

/** Snooze targets in the setter's own time. */
function snoozeOptions(now: Date): Array<{ label: string; at: Date }> {
  const inHour = new Date(now.getTime() + 3600_000);
  const tomorrow9 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0);
  const daysToMon = ((8 - now.getDay()) % 7) || 7;
  const monday9 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToMon, 9, 0);
  return [
    { label: '1 hour', at: inHour },
    { label: 'Tomorrow 9 AM', at: tomorrow9 },
    { label: 'Monday 9 AM', at: monday9 },
  ];
}

function TaskRow(props: { task: Task; onDone: (t: Task) => void; onSnooze: (t: Task, at: Date) => void; busy: boolean }) {
  const t = props.task;
  const [snooze, setSnooze] = useState(false);
  const overdue = new Date(t.dueAt).getTime() < Date.now();
  return (
    <div class="vd-task">
      <div style="min-width:0">
        <div class="row" style="gap:6px;flex-wrap:wrap">
          <span class={`chip ${t.kind === 'callback' ? 'chip-info' : ''}`}>{KIND_LABEL[t.kind] ?? t.kind}</span>
          <span class="vd-task-title">{t.title}</span>
        </div>
        <div class="vd-task-meta">
          {t.businessName ? <>{t.businessName} · </> : null}
          <span class={overdue ? 'vd-bad' : ''}>due {fmtDateTime(t.dueAt)}{overdue ? ` (${timeAgo(t.dueAt)})` : ''}</span>
        </div>
        {t.note ? <div class="vd-note">{t.note}</div> : null}
      </div>
      <div class="vd-task-actions">
        {t.membershipId ? (
          <button class="btn btn-sm btn-primary" disabled={props.busy} onClick={async () => {
            if ((await openMembership(t.membershipId!)) !== 'failed') navigate('/');
          }}>Open lead</button>
        ) : null}
        {snooze ? (
          <>
            {snoozeOptions(new Date()).map((o) => <button class="btn btn-sm" disabled={props.busy} onClick={() => { setSnooze(false); props.onSnooze(t, o.at); }}>{o.label}</button>)}
            <button class="btn btn-sm" onClick={() => setSnooze(false)}>Cancel</button>
          </>
        ) : <button class="btn btn-sm" disabled={props.busy} onClick={() => setSnooze(true)}>Snooze</button>}
        <button class="btn btn-sm" disabled={props.busy} onClick={() => props.onDone(t)}>Done</button>
      </div>
    </div>
  );
}

function Voicemails(props: { myId: number; refreshKey: number }) {
  const vms = useAsync(() => endpoints.voicemails(), [props.refreshKey]);
  const [showHandled, setShowHandled] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const list = (vms.data ?? []).filter((v) => showHandled || !v.handledAt).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const handled = async (id: number) => {
    setBusy(id);
    try {
      await endpoints.voicemailHandled(id);
      vms.setData((vms.data ?? []).map((v) => (v.id === id ? { ...v, handledAt: new Date().toISOString(), handledByName: 'you' } : v)));
    } catch (e) {
      toast(`Couldn't mark it handled: ${errorMessage(e)}`, 'bad');
      if (e instanceof ApiFailure && e.status === 403) vms.reload(); // someone else holds it now: refresh who can act
    } finally {
      setBusy(null);
    }
  };
  return (
    <div class="vd-group">
      <h3>Voicemails left for us <span class="chip">{(vms.data ?? []).filter((v) => !v.handledAt).length} new</span></h3>
      <ErrorBanner error={vms.error ? `Couldn't load voicemails: ${vms.error}` : null} />
      {vms.loading && !vms.data ? <p class="muted"><Spinner /> Loading…</p> : null}
      {vms.data && list.length === 0 ? <Empty>No voicemails waiting.</Empty> : null}
      {list.length ? (
        <div class="vd-table-wrap">
          {list.map((v) => {
            const act = voicemailRowActions(v, props.myId);
            return (
              <div class="vd-task">
                <div style="min-width:0">
                  <div class="vd-task-title">{v.businessName ?? 'Unknown caller'} <span class="mono muted">{fmtPhone(v.fromE164)}</span></div>
                  <div class="vd-task-meta">{fmtDateTime(v.createdAt)} · {timeAgo(v.createdAt)}{v.durationSec ? ` · ${fmtDuration(v.durationSec)}` : ''}{act.note ? ` · ${act.note}` : ''}</div>
                  <VoicemailAudio id={v.id} />
                </div>
                <div class="vd-task-actions">
                  {act.canMarkHandled ? <button class="btn btn-sm" disabled={busy === v.id} onClick={() => void handled(v.id)}>Mark handled</button> : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <label class="row" style="gap:6px;margin-top:6px;font-size:13px">
        <input type="checkbox" checked={showHandled} onChange={(e) => setShowHandled(e.currentTarget.checked)} /> Show handled
      </label>
    </div>
  );
}

export function Tasks(_props: { params: Record<string, string> }) {
  const me = useMe();
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const tasks = useAsync(() => endpoints.tasks({ scope, status: 'open' }), [scope]);
  const [busy, setBusy] = useState<number | null>(null);
  const [now, setNow] = useState(new Date());
  const [vmKey, setVmKey] = useState(0);

  useEffect(() => {
    const t = setInterval(() => { setNow(new Date()); if (document.visibilityState === 'visible') tasks.reload(); }, 120_000);
    return () => clearInterval(t);
  }, [tasks.reload]);

  const done = async (t: Task) => {
    setBusy(t.id);
    try {
      await endpoints.updateTask(t.id, { done: true });
      tasks.setData((tasks.data ?? []).filter((x) => x.id !== t.id));
      toast(`Done: ${t.title}`, 'ok');
      // Review 2026-10-24: finishing a voicemail's or a missed call's task marks the voicemail handled and the missed
      // call done (routes/tasks.ts): the box below and the Missed calls badge read that again.
      if (t.kind === 'voicemail') setVmKey((k) => k + 1);
      if (t.kind === 'voicemail' || t.kind === 'callback') void refreshMissed();
    } catch (e) {
      toast(`Couldn't complete the task: ${errorMessage(e)}`, 'bad');
    } finally { setBusy(null); }
  };
  const snooze = async (t: Task, at: Date) => {
    setBusy(t.id);
    try {
      const updated = await endpoints.updateTask(t.id, { dueAt: at.toISOString() });
      tasks.setData((tasks.data ?? []).map((x) => (x.id === t.id ? { ...x, ...(updated && typeof updated === 'object' ? updated : {}), dueAt: at.toISOString() } : x)));
      toast(`Snoozed to ${fmtDateTime(at.toISOString())}`, 'info');
    } catch (e) {
      toast(`Couldn't snooze: ${errorMessage(e)}`, 'bad');
    } finally { setBusy(null); }
  };

  const g = group(tasks.data ?? [], now);
  const section = (title: string, list: Task[], tone: string, empty: string) => (
    <div class="vd-group">
      <h3>{title} <span class={`chip ${tone}`}>{list.length}</span></h3>
      {list.length === 0 ? <p class="muted" style="margin:0">{empty}</p> : (
        <div class="vd-table-wrap">{list.map((t) => <TaskRow task={t} busy={busy === t.id} onDone={done} onSnooze={snooze} />)}</div>
      )}
    </div>
  );

  return (
    <div style="max-width:980px">
      <div class="vd-page-head">
        <div>
          <h1 style="margin:0">Tasks</h1>
          <p class="muted" style="margin:4px 0 0">Callbacks open the lead in the dialer. It still won't dial until you press Space.</p>
        </div>
        <div class="row" style="gap:8px">
          {me.role === 'admin' ? <Segmented value={scope} onChange={setScope} options={[{ value: 'mine', label: 'Mine' }, { value: 'all', label: 'Everyone' }]} /> : null}
          <button class="btn btn-sm" onClick={tasks.reload}>Refresh</button>
        </div>
      </div>
      <ErrorBanner error={tasks.error ? `Couldn't load tasks: ${tasks.error}` : null} />
      {tasks.loading && !tasks.data ? <p class="muted"><Spinner /> Loading tasks…</p> : null}
      {tasks.data ? (
        <>
          {section('Overdue', g.overdue, g.overdue.length ? 'chip-bad' : '', 'Nothing overdue.')}
          {section('Today', g.today, g.today.length ? 'chip-info' : '', 'Nothing else due today.')}
          {section('Upcoming', g.upcoming, '', 'Nothing scheduled after today.')}
        </>
      ) : null}
      <Voicemails myId={me.id} refreshKey={vmKey} />
    </div>
  );
}
